import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AmbossMode } from './amboss.config';
import { onAmbossDataReset } from './ambossDataReset';

export const AMBOSS_HISTORY_STORAGE_KEY = 'ambossHistory';
const MAX_AMBOSS_HISTORY_ENTRIES = 100;

export interface AmbossHistoryEntry {
  id: string;
  query: string;
  mode: AmbossMode;
  timestamp: number;
  // AMBOSS conversation id, captured once ai-mode swaps `?q=` for `?cid=`.
  // Only set in the Electron app; absent in the browser (see AmbossProvider).
  cid?: string;
}

export interface AmbossHistoryGroup {
  key: string;
  label: string;
  entries: AmbossHistoryEntry[];
}

const DAY_MS = 24 * 60 * 60 * 1000;

function isHistoryEntry(value: unknown): value is AmbossHistoryEntry {
  if (typeof value !== 'object' || value === null) return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.id === 'string' &&
    typeof entry.query === 'string' &&
    (entry.mode === 'ai' || entry.mode === 'search') &&
    typeof entry.timestamp === 'number' &&
    (entry.cid === undefined || typeof entry.cid === 'string')
  );
}

function load(): AmbossHistoryEntry[] {
  try {
    const raw = localStorage.getItem(AMBOSS_HISTORY_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isHistoryEntry) : [];
  } catch {
    return [];
  }
}

function save(entries: AmbossHistoryEntry[]): void {
  try {
    localStorage.setItem(AMBOSS_HISTORY_STORAGE_KEY, JSON.stringify(entries));
  } catch {
    /* ignore storage errors */
  }
}

function startOfDay(ts: number): number {
  const d = new Date(ts);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

export function groupHistory(
  entries: AmbossHistoryEntry[],
  now: number = Date.now()
): AmbossHistoryGroup[] {
  const sorted = [...entries].sort((a, b) => b.timestamp - a.timestamp);
  const todayStart = startOfDay(now);
  const weekStart = todayStart - 6 * DAY_MS;

  const today: AmbossHistoryEntry[] = [];
  const lastWeek: AmbossHistoryEntry[] = [];
  const months = new Map<string, AmbossHistoryEntry[]>();

  for (const entry of sorted) {
    if (entry.timestamp >= todayStart) {
      today.push(entry);
    } else if (entry.timestamp >= weekStart) {
      lastWeek.push(entry);
    } else {
      const d = new Date(entry.timestamp);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const bucket = months.get(key) ?? [];
      bucket.push(entry);
      months.set(key, bucket);
    }
  }

  const monthLabel = (ts: number) =>
    new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' }).format(new Date(ts));

  const groups: AmbossHistoryGroup[] = [];
  if (today.length) groups.push({ key: 'today', label: 'today', entries: today });
  if (lastWeek.length) groups.push({ key: 'last7Days', label: 'last7Days', entries: lastWeek });
  for (const [key, bucket] of months) {
    groups.push({ key, label: monthLabel(bucket[0].timestamp), entries: bucket });
  }
  return groups;
}

export function useAmbossHistory() {
  const [entries, setEntries] = useState<AmbossHistoryEntry[]>(() => load());

  useEffect(() => onAmbossDataReset(() => setEntries([])), []);

  useEffect(() => {
    save(entries);
  }, [entries]);

  const add = useCallback((query: string, mode: AmbossMode, timestamp: number = Date.now()) => {
    const id = `${timestamp}-${mode}-${query}`;
    setEntries((prev) => {
      const newest = prev[0];
      const rest = newest && newest.query === query && newest.mode === mode ? prev.slice(1) : prev;
      const entry: AmbossHistoryEntry = { id, query, mode, timestamp };
      return [entry, ...rest].slice(0, MAX_AMBOSS_HISTORY_ENTRIES);
    });
    return id;
  }, []);

  const bump = useCallback((id: string, timestamp: number = Date.now()) => {
    setEntries((prev) => {
      const target = prev.find((e) => e.id === id);
      if (!target) return prev;
      return [{ ...target, timestamp }, ...prev.filter((e) => e.id !== id)];
    });
  }, []);

  const remove = useCallback((id: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const updateCid = useCallback((id: string, cid: string) => {
    setEntries((prev) => {
      if (!prev.some((e) => e.id === id && e.cid !== cid)) return prev;
      return prev.map((e) => (e.id === id ? { ...e, cid } : e));
    });
  }, []);

  const groups = useMemo(() => groupHistory(entries), [entries]);

  return { entries, groups, add, bump, remove, updateCid };
}
